import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Table, Spinner } from 'react-bootstrap';
import { useFirestore } from '../contexts/FirestoreContext';

// --- helpers ---
const toDate = (ts) =>
  ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null);

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const fmtMDY = (d) =>
  d ? `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}` : '';

const iso = (d) =>
  d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10) : '';

const dash = (n) => (n > 0 ? n : '–');

const FACTORIES = ['Ford', 'GM'];

const factoryOf = (v) =>
  (v?.strFactory || '').toLowerCase().includes('ford') ? 'Ford' : 'GM';

// --- component ---
export default function ETAReport() {
  const { onSnapshotData } = useFirestore();

  const [vehicles, setVehicles] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // default date range
  const today = startOfDay(new Date());
  const defaultEnd = endOfDay(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 45));
  const [beginDate, setBeginDate] = useState(today);
  const [endDate, setEndDate] = useState(defaultEnd);

  // live data
  useEffect(() => {
    const uV = onSnapshotData('vehicles', (v) => setVehicles(v || []));
    const uS = onSnapshotData('salesOrders', (s) => setSalesOrders(s || []));
    const uC = onSnapshotData('customers', (c) => setCustomers(c || []));
    setLoading(false);
    return () => { uV && uV(); uS && uS(); uC && uC(); };
  }, [onSnapshotData]);

  // lookups
  const soByNbr = useMemo(() => {
    const m = new Map();
    (salesOrders || []).forEach(so => m.set(so.strSalesOrdNbr, so));
    return m;
  }, [salesOrders]);

  const custByNbr = useMemo(() => {
    const m = new Map();
    (customers || []).forEach(c => m.set(c.strCustNbr, c));
    return m;
  }, [customers]);

  // --- add these helpers (replace iso() completely) ---
  const formatYYYYMMDD = (d) => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const parseDateInput = (s) => {
    // "YYYY-MM-DD" -> local midnight (no timezone shift)
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  };


  // computations
  const { summary, perCustomer, totals } = useMemo(() => {
    const beg = startOfDay(beginDate);
    const end = endOfDay(endDate);

    const inDays = (n) => {
      const d = new Date(today);
      d.setDate(d.getDate() + n);
      return endOfDay(d);
    };

    const in7 = inDays(7);
    const in14 = inDays(14);
    const in30 = inDays(30);

    // summary skeleton
    const makeRow = () => ({ Ford: 0, GM: 0, Total: 0 });
    const sum = {
      range: makeRow(),
      d7: makeRow(),
      d14: makeRow(),
      d30: makeRow(),
    };

    // per-customer: { name: { range, d7, d14, d30 } }
    const perCust = new Map();
    const ensureCust = (name) => {
      if (!perCust.has(name)) perCust.set(name, { range: 0, d7: 0, d14: 0, d30: 0 });
      return perCust.get(name);
    };

    (vehicles || []).forEach(v => {
      const eta = toDate(v.dtManETA);
      if (!eta) return;

      // join sales order + require Active
      const so = soByNbr.get(v.strSalesOrdNbr);
      if (!so || so.strSalesOrdStatus !== 'Active') return;

      const customerName = custByNbr.get(so.strCustNbr)?.strCustName || '(Unknown Customer)';
      const f = factoryOf(v);

      // Range
      if (eta >= beg && eta <= end) {
        sum.range[f] += 1; sum.range.Total += 1;
        ensureCust(customerName).range += 1;
      }

      // next 7 / 14 / 30 from today (inclusive)
      if (eta >= today && eta <= in7) { sum.d7[f] += 1; sum.d7.Total += 1; ensureCust(customerName).d7 += 1; }
      if (eta >= today && eta <= in14) { sum.d14[f] += 1; sum.d14.Total += 1; ensureCust(customerName).d14 += 1; }
      if (eta >= today && eta <= in30) { sum.d30[f] += 1; sum.d30.Total += 1; ensureCust(customerName).d30 += 1; }
    });

    // sort customers A→Z
    const rows = Array.from(perCust.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([customer, counts]) => ({ customer, ...counts }));

    // totals row for customer table (sum the columns)
    const t = rows.reduce((acc, r) => {
      acc.range += r.range;
      acc.d7 += r.d7;
      acc.d14 += r.d14;
      acc.d30 += r.d30;
      return acc;
    }, { range: 0, d7: 0, d14: 0, d30: 0 });

    return { summary: sum, perCustomer: rows, totals: t };
  }, [vehicles, soByNbr, custByNbr, beginDate, endDate]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <Card>
      <Card.Body>
        <div className="text-center mb-3">
          <div className="fw-bold">Hardy Automotive Fleet Group</div>
          <div>ETA Reports</div>
        </div>

        {/* Date pickers */}
        <Row className="g-3 align-items-end mb-3">
          <Col xs={12} md={3}>
            <Form.Label>Begin Date</Form.Label>
            <Form.Control
              type="date"
              value={formatYYYYMMDD(beginDate)}
              onChange={(e) => setBeginDate(startOfDay(parseDateInput(e.target.value)))}
            />
          </Col>
          <Col xs={12} md={3}>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={formatYYYYMMDD(endDate)}
              onChange={(e) => setEndDate(endOfDay(parseDateInput(e.target.value)))}
            />
          </Col>
          <Col xs={12} md={6}>
            <div className="mt-3 mt-md-0">
              <strong>Begin:</strong> {fmtMDY(beginDate)} &nbsp; &nbsp;
              <strong>End:</strong> {fmtMDY(endDate)}
            </div>
          </Col>
        </Row>

        {/* Top summary table */}
        <Table bordered responsive className="mb-4">
          <thead>
            <tr>
              <th style={{ width: '45%' }}></th>
              <th className="text-center">Ford</th>
              <th className="text-center">GM</th>
              <th className="text-center">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="fw-semibold">ETA within Date Range</td>
              {FACTORIES.map(f => <td key={`range-${f}`} className="text-center">{summary.range[f]}</td>)}
              <td className="text-center fw-bold">{summary.range.Total}</td>
            </tr>
            <tr>
              <td className="fw-semibold">ETA within 7 Days</td>
              {FACTORIES.map(f => <td key={`d7-${f}`} className="text-center">{dash(summary.d7[f])}</td>)}
              <td className="text-center fw-bold">{dash(summary.d7.Total)}</td>
            </tr>
            <tr>
              <td className="fw-semibold">ETA within 14 Days</td>
              {FACTORIES.map(f => <td key={`d14-${f}`} className="text-center">{dash(summary.d14[f])}</td>)}
              <td className="text-center fw-bold">{dash(summary.d14.Total)}</td>
            </tr>
            <tr>
              <td className="fw-semibold">ETA within 30 Days</td>
              {FACTORIES.map(f => <td key={`d30-${f}`} className="text-center">{dash(summary.d30[f])}</td>)}
              <td className="text-center fw-bold">{dash(summary.d30.Total)}</td>
            </tr>
          </tbody>
        </Table>

        {/* Customer detail table */}
        <div className="fw-bold mb-2">Customers</div>
        <Table bordered responsive>
          <thead>
            <tr>
              <th style={{ width: '45%' }}>Customer</th>
              <th className="text-center">Date Range</th>
              <th className="text-center">7 Days</th>
              <th className="text-center">14 Days</th>
              <th className="text-center">30 Days</th>
            </tr>
          </thead>
          <tbody>
            {perCustomer.map(r => (
              <tr key={r.customer}>
                <td>{r.customer}</td>
                <td className="text-center">{dash(r.range)}</td>
                <td className="text-center">{dash(r.d7)}</td>
                <td className="text-center">{dash(r.d14)}</td>
                <td className="text-center">{dash(r.d30)}</td>
              </tr>
            ))}
            <tr>
              <td className="fw-bold">Total</td>
              <td className="text-center fw-bold">{totals.range}</td>
              <td className="text-center fw-bold">{totals.d7}</td>
              <td className="text-center fw-bold">{totals.d14}</td>
              <td className="text-center fw-bold">{totals.d30}</td>
            </tr>
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}
